import React, { useState, useEffect } from "react";
import editimg from "../../assets/img/edit.svg";
import deleteimg from "../../assets/img/delete.svg";
import searchimg from "../../assets/img/search.svg";
import downloadimg from "../../assets/img/download.svg";
import plusimg from "../../assets/img/plus.svg";
import chevrondown from "../../assets/img/chevron-down.svg";
import threedots from "../../assets/img/three-dots-vertical.svg";
import chevronup from "../../assets/img/chevron-up.svg";
import eyeimg from "../../assets/img/eye.svg";
import { fetchApi } from "../../api";
import * as XLSX from "xlsx";

function AddressMaster() {
  const godrejStyles = {
    primary: "#810055",
    secondary: "#707070",
    font: "GB-BodyCopy",
  };

  const [addresses, setAddresses] = useState([]);
  const [userNameMap, setUserNameMap] = useState({});

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const data = await fetchApi("/api/addressmaster/all");
        const addressArray = Array.isArray(data) ? data : data.addresses || data.data || [];
        const formattedData = addressArray.map((item, index) => ({ srno: index + 1, ...item }));
        setAddresses(formattedData);
      } catch (error) {
        console.error("Error fetching addresses:", error);
        setAddresses([]);
      }
    };
    const fetchUserNames = async () => {
      try {
        const data = await fetchApi("/api/usermaster/all");
        const userArray = Array.isArray(data) ? data : data.users || data.data || [];
        const map = {};
        userArray.forEach(u => {
          if (u.um_email && u.um_user_name) map[u.um_email.toLowerCase()] = u.um_user_name;
        });
        setUserNameMap(map);
      } catch (error) {
        console.error("Error fetching user names:", error);
      }
    };
    fetchAddresses();
    fetchUserNames();
  }, []);

  const [formData, setFormData] = useState({
    am_status: "Active",
    am_address1: "",
    am_address2: "",
    am_city: "",
    am_pin_code: "",
    am_state: "",
    am_country: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editOriginal, setEditOriginal] = useState({});
  const [editFormSnapshot, setEditFormSnapshot] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [viewAddress, setViewAddress] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showConfirmUpdate, setShowConfirmUpdate] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);

  // Column filter state
  const [columnFilters, setColumnFilters] = useState({});
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterDraft, setFilterDraft] = useState({ type: "Contains", value: "" });

  const updateFilterLive = (colKey, draft) => {
    setFilterDraft(draft);
  };

  // Column resize state
  const [columnWidths, setColumnWidths] = useState({});
  const resizingCol = React.useRef(null);
  const startX = React.useRef(0);
  const startWidth = React.useRef(0);

  const handleMouseDown = (e, colKey) => {
    e.preventDefault();
    resizingCol.current = colKey;
    startX.current = e.clientX;
    const th = e.target.parentElement;
    startWidth.current = th.offsetWidth;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!resizingCol.current) return;
    const diff = e.clientX - startX.current;
    const newWidth = Math.max(50, startWidth.current + diff);
    setColumnWidths(prev => ({ ...prev, [resizingCol.current]: newWidth }));
  };

  const handleMouseUp = () => {
    resizingCol.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [hoveredCol, setHoveredCol] = useState(null);

  const handleSort = (colKey) => {
    setSortConfig(prev => ({
      key: colKey,
      direction: prev.key === colKey && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const columns = [
    { key: "am_status", label: "Status" },
    { key: "am_address1", label: "Office No./Floor/Plant/Bldg No." },
    { key: "am_address2", label: "Street/Road (Name Or No.)/Location" },
    { key: "am_city", label: "City" },
    { key: "am_pin_code", label: "Pincode" },
    { key: "am_state", label: "State" },
    { key: "am_country", label: "Country" },
    { key: "am_inserted_by", label: "Inserted By" },
    { key: "am_inserted_on", label: "Inserted On" },
    { key: "am_modified_by", label: "Modified By" },
    { key: "am_modified_on", label: "Modified On" },
  ];

  const applyColumnFilter = (colKey) => {
    if (filterDraft.value || filterDraft.type === "Blank" || filterDraft.type === "Not Blank") {
      setColumnFilters(prev => ({ ...prev, [colKey]: { ...filterDraft } }));
    }
    setActiveFilter(null);
    setCurrentPage(0);
  };

  const resetColumnFilter = (colKey) => {
    setColumnFilters(prev => { const n = { ...prev }; delete n[colKey]; return n; });
    setFilterDraft({ type: "Contains", value: "" });
    setActiveFilter(null);
    setCurrentPage(0);
  };

  const matchesFilter = (val, filter) => {
    const cellVal = String(val || "").toLowerCase();
    const filterVal = (filter.value || "").toLowerCase();
    switch (filter.type) {
      case "Contains": return cellVal.includes(filterVal);
      case "Not Contains": return !cellVal.includes(filterVal);
      case "Equals": return cellVal === filterVal;
      case "Not Equals": return cellVal !== filterVal;
      case "Starts With": return cellVal.startsWith(filterVal);
      case "Ends With": return cellVal.endsWith(filterVal);
      case "Blank": return cellVal.trim() === "";
      case "Not Blank": return cellVal.trim() !== "";
      default: return true;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  };

  const resolveUserName = (value) => {
    if (!value) return "";
    if (value.includes("@")) return userNameMap[value.toLowerCase()] || value;
    return value;
  };

  const getCellValue = (addr, key) => {
    if (key === "am_inserted_on" || key === "am_modified_on") {
      return formatDateTime(addr[key]);
    }
    if (key === "am_inserted_by" || key === "am_modified_by") {
      return resolveUserName(addr[key]);
    }
    return addr[key] || "";
  };

  const filteredAddresses = addresses.filter(addr => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matches = columns.some(col => String(getCellValue(addr, col.key) || "").toLowerCase().includes(term));
      if (!matches) return false;
    }
    for (const [colKey, filter] of Object.entries(columnFilters)) {
      if (!matchesFilter(getCellValue(addr, colKey), filter)) return false;
    }
    return true;
  }).sort((a, b) => {
    if (!sortConfig.key) {
      if (a.am_status !== b.am_status) {
        return a.am_status === 'Active' ? -1 : 1;
      }
      const aTime = new Date(a.am_modified_on || a.am_inserted_on || 0).getTime();
      const bTime = new Date(b.am_modified_on || b.am_inserted_on || 0).getTime();
      return bTime - aTime;
    }
    const aVal = String(getCellValue(a, sortConfig.key) || "").toLowerCase();
    const bVal = String(getCellValue(b, sortConfig.key) || "").toLowerCase();
    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const numberOfPages = () => Math.ceil(filteredAddresses.length / pageSize);

  // GS-SEC-006: Sanitize cell values to prevent CSV formula injection
  const sanitizeForCSV = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    if (/^[=+\-@\t\r|]/.test(str)) return "'" + str;
    return str;
  };

  const handleDownload = () => {
    const exportData = filteredAddresses.map(addr => ({
      "Status": sanitizeForCSV(addr.am_status),
      "Office No./Floor/Plant/Bldg No.": sanitizeForCSV(addr.am_address1),
      "Street/Road/Location": sanitizeForCSV(addr.am_address2),
      "City": sanitizeForCSV(addr.am_city),
      "Pincode": sanitizeForCSV(addr.am_pin_code),
      "State": sanitizeForCSV(addr.am_state),
      "Country": sanitizeForCSV(addr.am_country),
      "Inserted By": sanitizeForCSV(addr.am_inserted_by),
      "Inserted On": sanitizeForCSV(formatDateTime(addr.am_inserted_on)),
      "Modified By": sanitizeForCSV(addr.am_modified_by),
      "Modified On": sanitizeForCSV(formatDateTime(addr.am_modified_on)),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "AddressMaster");
    XLSX.writeFile(wb, "AddressMasterData.xlsx");
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    if (id === 'am_address1' || id === 'am_address2') {
      // Allow only alphanumeric, spaces, commas, periods, and hyphens
      const sanitized = value.replace(/[^a-zA-Z0-9 ,.\-]/g, '');
      if (sanitized.length > 200) return;
      setFormData(prev => ({ ...prev, [id]: sanitized }));
      return;
    }
    if (id === 'am_city' || id === 'am_state' || id === 'am_country') {
      const alphaValue = value.replace(/[^a-zA-Z ]/g, '');
      if (alphaValue.length > 50) return;
      setFormData(prev => ({ ...prev, [id]: alphaValue }));
      return;
    }
    if (id === 'am_pin_code') {
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue.length > 6) return;
      setFormData(prev => ({ ...prev, [id]: numericValue }));
      if (numericValue && numericValue.length !== 6) {
        setValidationErrors(prev => ({ ...prev, am_pin_code: 'Please check Pincode' }));
      } else {
        setValidationErrors(prev => { const n = { ...prev }; delete n.am_pin_code; return n; });
      }
      return;
    }
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const loggedInUser = sessionStorage.getItem("userDisplayName") || sessionStorage.getItem("userEmail") || "";

  const validateForm = () => {
    const errors = {};
    const mandatoryFields = [
      { key: 'am_address1', label: 'Office No./Floor/Plant/Building No.' },
      { key: 'am_address2', label: 'Street/Road (Name Or No.)/Location' },
      { key: 'am_city', label: 'City/District' },
      { key: 'am_pin_code', label: 'Pincode' },
      { key: 'am_state', label: 'State/Province' },
      { key: 'am_country', label: 'Country' },
    ];
    mandatoryFields.forEach(field => {
      if (!formData[field.key] || String(formData[field.key]).trim() === '') {
        errors[field.key] = `${field.label} is mandatory`;
      }
    });
    if (formData.am_pin_code && String(formData.am_pin_code).length !== 6) {
      errors.am_pin_code = 'Please check Pincode';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    try {
      const now = new Date().toISOString();
      let payload = {
        ...formData,
        am_pin_code: parseInt(formData.am_pin_code) || 0,
      };

      if (isEditing) {
        payload.am_id = editId;
        payload.am_inserted_by = editOriginal.am_inserted_by;
        payload.am_inserted_on = editOriginal.am_inserted_on;
        payload.am_modified_by = loggedInUser;
        payload.am_modified_on = now;
      } else {
        payload.am_inserted_by = loggedInUser;
        payload.am_inserted_on = now;
        payload.am_modified_by = loggedInUser;
        payload.am_modified_on = now;
      }

      if (isEditing) {
        await fetchApi("/api/addressmaster/UpdateAddressMaster", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      } else {
        await fetchApi("/api/addressmaster/save", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }

      // Re-fetch to get latest data from DB
      const data = await fetchApi("/api/addressmaster/all");
      const addressArray = Array.isArray(data) ? data : data.addresses || data.data || [];
      setAddresses(addressArray.map((item, index) => ({ srno: index + 1, ...item })));

      if (isEditing) {
        setSuccessMessage("Address Updated Successfully.");
      } else {
        setSuccessMessage("New Address Created Successfully.");
      }

      resetForm();
      setShowForm(false);
    } catch (error) {
      console.error("Error saving address:", error);
      alert("Failed to save address. Please try again.");
    }
  };

  const resetForm = () => {
    setFormData({ am_status: "Active", am_address1: "", am_address2: "", am_city: "", am_pin_code: "", am_state: "", am_country: "" });
    setValidationErrors({});
    setIsEditing(false);
    setEditId(null);
    setViewId(null);
    setViewAddress(null);
  };

  const handleEdit = (address) => {
    const snapshot = {
      am_status: address.am_status || "Active",
      am_address1: address.am_address1,
      am_address2: address.am_address2,
      am_city: address.am_city,
      am_pin_code: address.am_pin_code,
      am_state: address.am_state,
      am_country: address.am_country,
    };
    setFormData(snapshot);
    setEditFormSnapshot(snapshot);
    setEditOriginal({ am_inserted_by: address.am_inserted_by, am_inserted_on: address.am_inserted_on });
    setIsEditing(true);
    setEditId(address.am_id);
    setShowForm(true);
  };

  const [deleteError, setDeleteError] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);
  const [noChangesError, setNoChangesError] = useState(false);

  const handleDelete = async (id) => {
    // Check if address is used in any signature
    try {
      const res = await fetchApi(`/api/addressmaster/check-in-use/${id}`);
      console.log('check-in-use response:', res);
      if (res?.inUse) {
        setDeleteError("This address already in use.");
        return;
      }
    } catch (error) {
      console.error("Error checking address usage:", error);
    }
    setShowConfirmDelete(id);
  };

  const confirmDelete = async () => {
    const id = showConfirmDelete;
    setShowConfirmDelete(null);
    try {
      await fetchApi(`/api/addressmaster/${id}`, { method: "DELETE" });
      setAddresses(prev => prev.filter(addr => addr.am_id !== id));
      setSuccessMessage("Address Deleted Successfully.");
    } catch (error) {
      console.error("Error deleting address:", error);
      alert("Failed to delete address. Please try again.");
    }
  };

  const btnStyle = {
    backgroundColor: godrejStyles.primary,
    color: "white",
    border: "none",
    padding: "3px 15px",
    borderRadius: "25px",
  };


  return (
    <div style={{ fontFamily: godrejStyles.font, paddingRight: "10px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingLeft: "10px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <h1 style={{ fontSize: "18px", fontWeight: "400", color: "#333", margin: "0", marginRight: "15px" }}>Address Master</h1>
          <button onClick={() => { resetForm(); setShowForm(true); }} className="btn" style={{...btnStyle, textTransform: 'none', fontSize: '14px', fontWeight: '400'}}>
            <img src={plusimg} alt="Add" style={{ width: '16px', height: '21px', marginRight: '8px', filter: 'brightness(0) invert(1)' }} />
            Add New Address
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <input 
            type="text" 
            placeholder="Search here..." 
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
            style={{ 
              width: "400px", 
              height: "35px", 
              padding: "4px 34px", 
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px",
              backgroundImage: `url(${searchimg})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "10px center",
              backgroundSize: "16px 16px"
            }} 
          />
          <img 
            src={downloadimg} 
            alt="Download" 
            title="Download" 
            onClick={handleDownload}
            style={{ 
              width: "20px", 
              height: "20px", 
              marginLeft: "15px",
              cursor: "pointer"
            }} 
          />
        </div>
      </div>

      {/* No Changes Popup */}
      {noChangesError && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1003
        }}>
          <div style={{
            backgroundColor: 'white', padding: '30px 40px', borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)', textAlign: 'center', minWidth: '300px'
          }}>
            <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>No Changes Found.</p>
            <button type="button" className="btn" onClick={() => setNoChangesError(false)} style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>OK</button>
          </div>
        </div>
      )}

      {/* Confirm Delete Popup */}
      {showConfirmDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1003
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px 40px',
            borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            textAlign: 'center',
            minWidth: '300px'
          }}>
            <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>Are you sure you want to delete this address?</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
              <button type="button" className="btn" onClick={() => setShowConfirmDelete(null)} style={{ backgroundColor: 'transparent', color: '#707070', border: '1px solid #ccc', padding: '3px 15px', borderRadius: '25px', boxShadow: 'none', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>No</button>
              <button type="button" className="btn" onClick={confirmDelete} style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Error Popup */}
      {deleteError && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1002
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px 40px',
            borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            textAlign: 'center',
            minWidth: '300px'
          }}>
            <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>{deleteError}</p>
            <button type="button" className="btn" onClick={() => setDeleteError(null)} style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>OK</button>
          </div>
        </div>
      )}

      {/* Confirm Update Popup */}
      {showConfirmUpdate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1003
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px 40px',
            borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            textAlign: 'center',
            minWidth: '300px'
          }}>
            <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>Are you sure, do you want to update Address ?</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
              <button type="button" className="btn" onClick={() => setShowConfirmUpdate(false)} style={{ backgroundColor: 'transparent', color: '#707070', border: '1px solid #ccc', padding: '3px 15px', borderRadius: '25px', boxShadow: 'none', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>No</button>
              <button type="button" className="btn" onClick={() => { setShowConfirmUpdate(false); handleSave(); }} style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>Update</button>
            </div>
          </div>
        </div>
      )}

      {/* Success Message Popup */}
      {successMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1002
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px 40px',
            borderRadius: '4px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            textAlign: 'center',
            minWidth: '300px'
          }}>
            <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>{successMessage}</p>
            <button type="button" className="btn" onClick={() => setSuccessMessage(null)} style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>OK</button>
          </div>
        </div>
      )}

      {/* View Address Popup */}
      {viewAddress && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '0',
            borderRadius: '0',
            width: '70%',
            maxWidth: '800px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '15px', borderBottom: '1px solid #ccc', padding: '20px 20px 15px 20px' }}>
              <h2 style={{ color: '#333', fontSize: '18px', fontWeight: '500', margin: 0 }}>View Address</h2>
              <button 
                onClick={() => setViewAddress(null)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#707070', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ×
              </button>
            </div>
            <div style={{ width: '100%', padding: '15px 20px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', margin: 0, padding: 0 }}>
                {[
                  ["Status", viewAddress.am_status],
                  ["Office No./Floor/Plant/Bldg No.", viewAddress.am_address1],
                  ["Street/Road (Name Or No.)/Location", viewAddress.am_address2],
                  ["City/District", viewAddress.am_city],
                  ["Pincode", viewAddress.am_pin_code],
                  ["State/Province", viewAddress.am_state],
                  ["Country", viewAddress.am_country],
                  ["Inserted By", resolveUserName(viewAddress.am_inserted_by)],
                  ["Inserted On", formatDateTime(viewAddress.am_inserted_on)],
                  ["Modified By", resolveUserName(viewAddress.am_modified_by)],
                  ["Modified On", formatDateTime(viewAddress.am_modified_on)],
                ].map(([label, value]) => (
                  <div style={{ width: '33.33%', padding: '0 10px', paddingBottom: '12px' }} key={label}>
                    <label style={{ color: '#707070', marginBottom: '2px', display: 'block', fontSize: '12px' }}>{label}</label>
                    <div style={{
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      padding: '10px 14px',
                      fontSize: '14px',
                      width: '100%',
                      height: '38px',
                      backgroundColor: '#f9f9f9',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden'
                    }} title={String(value || '-')}>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'auto', width: '100%', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>{value || '-'}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                <button type="button" className="btn" onClick={() => setViewAddress(null)} style={{ backgroundColor: 'transparent', color: '#707070', border: '1px solid #ccc', padding: '3px 15px', borderRadius: '25px', boxShadow: 'none', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '0',
            borderRadius: '0',
            width: '70%',
            maxWidth: '800px',
            maxHeight: '80%',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '15px', borderBottom: '1px solid #ccc', padding: '20px 20px 15px 20px' }}>
              <h2 style={{ color: '#333', fontSize: '18px', fontWeight: '500', margin: 0 }}>
                {isEditing ? 'Edit Address' : 'Add New Address'}
              </h2>
              <button 
                onClick={() => { resetForm(); setShowForm(false); }}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#707070', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '0' }}>
              <form style={{ padding: '0', margin: '0' }}>
                <div style={{ width: '100%', padding: '20px', marginTop: '10px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', margin: 0, padding: 0 }}>
                    <div style={{ width: '33.33%', padding: '0 10px', paddingBottom: '20px' }}>
                      <label className="form-label" style={{ color: '#707070', marginBottom: '8px', display: 'block' }}>Status</label>
                      <div style={{ display: 'flex', alignItems: 'center', marginTop: '3px' }}>
                        <input 
                          type="checkbox" 
                          checked={formData.am_status === "Active"}
                          onChange={(e) => setFormData(prev => ({ ...prev, am_status: e.target.checked ? "Active" : "Inactive" }))}
                          style={{ display: 'none' }}
                          id="am-status-toggle"
                        />
                        <label 
                          htmlFor="am-status-toggle"
                          style={{
                            width: '29px',
                            height: '13px',
                            backgroundColor: formData.am_status === "Active" ? '#33C037' : '#707070',
                            borderRadius: '13px',
                            position: 'relative',
                            cursor: 'pointer',
                            display: 'inline-block',
                            transition: 'background-color 0.3s',
                            marginTop: '8px'
                          }}
                        >
                          <span style={{
                            position: 'absolute',
                            top: '-1px',
                            left: formData.am_status === "Active" ? '15px' : '-1px',
                            width: '15px',
                            height: '15px',
                            backgroundColor: '#fff',
                            border: `1px solid ${formData.am_status === "Active" ? '#33C037' : '#707070'}`,
                            borderRadius: '50%',
                            transition: 'left 0.3s, border-color 0.3s'
                          }}></span>
                        </label>
                        <label style={{ marginLeft: '10px', color: '#707070' }}>{formData.am_status}</label>
                      </div>
                    </div>
                    {[
                      ["am_address1", "Office No./Floor/Plant/Building No."],
                      ["am_address2", "Street/Road (Name Or No.)/Location"],
                      ["am_city", "City/District"],
                      ["am_pin_code", "Pincode"],
                      ["am_state", "State/Province"],
                      ["am_country", "Country"],
                    ].map(([id, label]) => (
                      <div style={{ width: '33.33%', padding: '0 10px', paddingBottom: '20px' }} key={id}>
                        <input 
                          className="form-control" 
                          id={id} 
                          type="text" 
                          placeholder={label}
                          value={formData[id]} 
                          onChange={handleInputChange}
                          style={{ border: `1px solid ${validationErrors[id] ? 'red' : '#ccc'}`, borderRadius: '4px', padding: '15px 18px', fontSize: '16px', width: '100%', height: '50px' }}
                        />
                        {validationErrors[id] && <span style={{ color: 'red', fontSize: '12px' }}>{validationErrors[id]}</span>}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem', width: '100%' }}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button type="button" className="btn" onClick={() => { resetForm(); setShowForm(false); }} style={{ backgroundColor: 'transparent', color: '#707070', border: '1px solid #ccc', padding: '3px 15px', borderRadius: '25px', boxShadow: 'none', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>Cancel</button>
                      <button type="button" className="btn" onClick={() => { if (isEditing && editFormSnapshot) { setFormData(editFormSnapshot); } else { setFormData({ am_status: "Active", am_address1: "", am_address2: "", am_city: "", am_pin_code: "", am_state: "", am_country: "" }); } setValidationErrors({}); }} style={{ backgroundColor: 'transparent', color: '#810055', border: 'none', padding: '3px 15px', borderRadius: '25px', boxShadow: 'none', fontWeight: '600', textTransform: 'none', fontSize: '14px' }}>Reset Fields</button>
                    </div>
                    <div>
                      <button type="button" className="btn me-2" onClick={() => { if (!validateForm()) return; if (isEditing) { if (JSON.stringify(formData) === JSON.stringify(editFormSnapshot)) { setNoChangesError(true); return; } setShowConfirmUpdate(true); } else { handleSave(); } }} style={{ backgroundColor: godrejStyles.primary, color: 'white', border: 'none', padding: '3px 15px', borderRadius: '25px', fontWeight: '600', textTransform: 'none', fontSize: '14px', minWidth: '80px' }}>{isEditing ? "Update" : "Add"}</button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div>
        {Object.keys(columnFilters).length > 0 && (
          <div className="filterList" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', padding: '5px 10px' }}>
            {Object.entries(columnFilters).map(([colKey, filter]) => {
              const col = columns.find(c => c.key === colKey);
              return (
                <div key={colKey} className="filter-col pr-0 me-2 mb-2" style={{ display: 'flex', alignItems: 'center', backgroundColor: '#F9F2F6', borderRadius: '25px', padding: '2px 8px', fontSize: '13px' }}>
                  <span className="ps-2 btn-text">{col?.label || colKey} : {filter.type === 'Blank' || filter.type === 'Not Blank' ? filter.type : filter.value}</span>
                  <div onClick={() => resetColumnFilter(colKey)} style={{ cursor: 'pointer', marginLeft: '4px' }}>
                    <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 16 16" height="25" width="25" xmlns="http://www.w3.org/2000/svg"><path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"></path></svg>
                  </div>
                </div>
              );
            })}
            <div>
              <button type="button" className="btn" style={{ backgroundColor: 'white', color: '#810055', border: '1px solid #810055', padding: '3px 15px', borderRadius: '25px', textTransform: 'none', fontSize: '14px', fontWeight: '500', marginLeft: '6px' }} onClick={() => { setColumnFilters({}); setCurrentPage(0); }}>Clear All</button>
            </div>
          </div>
        )}
        <div style={{ maxHeight: "400px", overflowY: "scroll", overflowX: "scroll", padding: "0px 0px 10px 10px", boxSizing: "border-box", backgroundColor: "#fff", marginTop: "5px" }}>
          <table className="table table-hover mb-0" style={{ width: "100%", minWidth: "1800px", borderCollapse: "separate", borderSpacing: "0 3px" }}>
            <thead style={{ backgroundColor: godrejStyles.secondary, color: "#fff", fontSize: "0.9rem", position: "sticky", top: 0, zIndex: 2, verticalAlign: "top", height: "36px", fontWeight: "400", whiteSpace: "nowrap" }}>
              <tr>
                {columns.map(col => (
                  <th key={col.key} onMouseEnter={() => setHoveredCol(col.key)} onMouseLeave={() => setHoveredCol(null)} style={{ height: "36px", padding: "8px 10px", fontWeight: "400", whiteSpace: "nowrap", position: "relative", textAlign: "left", width: columnWidths[col.key] ? `${columnWidths[col.key]}px` : "auto", backgroundColor: godrejStyles.secondary, boxShadow: "0 -4px 0 0 #fff" }}>
                    <span style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                      {col.label}
                      <img
                        src={chevronup}
                        alt="sort"
                        onClick={() => handleSort(col.key)}
                        style={{ width: "12px", height: "12px", marginLeft: "5px", filter: "brightness(0) invert(1)", cursor: "pointer", transform: sortConfig.key === col.key && sortConfig.direction === "desc" ? "rotate(180deg)" : "none", opacity: hoveredCol === col.key || sortConfig.key === col.key ? 1 : 0, transition: "opacity 0.2s" }}
                      />
                      <img
                        src={threedots}
                        alt="filter"
                        onClick={() => {
                          if (activeFilter === col.key) {
                            setActiveFilter(null);
                          } else {
                            setActiveFilter(col.key);
                            setFilterDraft(columnFilters[col.key] || { type: "Contains", value: "" });
                          }
                        }}
                        style={{ width: "14px", height: "14px", marginLeft: "5px", filter: "brightness(0) invert(1)", cursor: "pointer", opacity: hoveredCol === col.key || activeFilter === col.key || columnFilters[col.key] ? 1 : 0, transition: "opacity 0.2s" }}
                      />
                    </span>
                    {activeFilter === col.key && (
                      <div style={{ position: "absolute", top: "36px", left: "0", zIndex: 1000, background: "#fff", border: "1px solid #ccc", borderRadius: "4px", padding: "12px", minWidth: "200px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
                        <select
                          className="form-control"
                          value={filterDraft.type}
                          onChange={(e) => updateFilterLive(col.key, { ...filterDraft, type: e.target.value })}
                          style={{ fontSize: "13px", marginBottom: "8px", appearance: "none", WebkitAppearance: "none", MozAppearance: "none", backgroundImage: `url(${chevrondown})`, backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center", backgroundSize: "12px 12px", paddingRight: "30px" }}
                        >
                          {["Contains", "Not Contains", "Equals", "Not Equals", "Starts With", "Ends With", "Blank", "Not Blank"].map(opt => (
                            <option key={opt}>{opt}</option>
                          ))}
                        </select>
                        {!["Blank", "Not Blank"].includes(filterDraft.type) && (
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Filter..."
                            value={filterDraft.value}
                            onChange={(e) => updateFilterLive(col.key, { ...filterDraft, value: e.target.value })}
                            style={{ fontSize: "13px", marginBottom: "8px" }}
                          />
                        )}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                          <span onClick={() => resetColumnFilter(col.key)} style={{ cursor: "pointer", color: "#707070", fontSize: "12px", fontWeight: "600" }}>RESET</span>
                          <span onClick={() => applyColumnFilter(col.key)} style={{ cursor: "pointer", color: "#810055", fontSize: "12px", fontWeight: "600" }}>APPLY</span>
                        </div>
                      </div>
                    )}
                    <div onMouseDown={(e) => handleMouseDown(e, col.key)} style={{ position: "absolute", right: 0, top: "25%", height: "50%", width: "2px", backgroundColor: "#a0a0a0", cursor: "col-resize" }}></div>
                  </th>
                ))}
                <th style={{ height: "36px", padding: "8px 10px", fontWeight: "400", whiteSpace: "nowrap", textAlign: "left", backgroundColor: godrejStyles.secondary, position: "sticky", right: 0, zIndex: 3 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAddresses.length > 0 ? (
                (filteredAddresses.slice(currentPage * pageSize, (currentPage + 1) * pageSize))
                  .map(address => (
                    <tr key={address.am_id} style={{ border: "1px solid #e0e0e0", backgroundColor: "#fff" }}>
                      {columns.map((col, idx) => (
                        <td key={col.key} style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0", ...(idx === 0 ? { borderLeft: "1px solid #e0e0e0" } : {}) }}>
                          {col.key === 'am_status' ? (
                            <div style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              gap: "6px",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              backgroundColor: address.am_status?.toLowerCase() === 'active' ? 'rgba(25, 135, 84, 0.1)' : 'rgba(112, 112, 112, 0.1)'
                            }}>
                              <img 
                                src={address.am_status?.toLowerCase() === 'active' ? require('../../assets/img/check-circle-fill.svg').default : require('../../assets/img/x-circle-fill.svg').default}
                                alt={address.am_status}
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  filter: address.am_status?.toLowerCase() === 'active' 
                                    ? 'brightness(0) saturate(100%) invert(35%) sepia(85%) saturate(1352%) hue-rotate(87deg) brightness(104%) contrast(97%)'
                                    : 'brightness(0) saturate(100%) invert(45%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)'
                                }}
                              />
                              <span style={{
                                color: address.am_status?.toLowerCase() === 'active' ? '#198754' : '#707070',
                                fontWeight: "500",
                                fontSize: "13px"
                              }}>
                                {address.am_status}
                              </span>
                            </div>
                          ) : (
                            getCellValue(address, col.key)
                          )}
                        </td>
                      ))}
                      <td style={{ whiteSpace: "nowrap", textAlign: "left", padding: "8px 10px", border: "1px solid #e0e0e0", borderLeft: "none", position: "sticky", right: 0, backgroundColor: "#fff", zIndex: 1 }}>
                        <button onClick={() => setViewAddress(address)} className="btn btn-sm me-1" style={{ background: "none", border: "none", padding: 0, boxShadow: "none" }}>
                          <img src={eyeimg} alt="View" title="View" style={{ width: "20px", height: "20px", cursor: "pointer", filter: "invert(45%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)" }} />
                        </button>
                        <button onClick={() => handleEdit(address)} className="btn btn-sm me-1" style={{ background: "none", border: "none", padding: 0, boxShadow: "none" }}>
                          <img src={editimg} alt="Edit" title="Edit" style={{ width: "20px", height: "20px", cursor: "pointer" }} />
                        </button>
                        <button onClick={() => handleDelete(address.am_id)} className="btn btn-sm" style={{ background: "none", border: "none", padding: 0, boxShadow: "none" }}>
                          <img src={deleteimg} alt="Delete" title="Delete" style={{ width: "20px", height: "20px", cursor: "pointer", filter: "invert(45%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)" }} />
                        </button>
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan={columns.length + 1} className="text-center py-4" style={{ color: godrejStyles.secondary }}>{searchTerm ? "No matching records found" : "No record found"}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: '20px', backgroundColor: '#fff' }}>
          <div style={{ padding: '15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '14px', color: '#707070' }}>Items per page</span>
              <input 
                min="1" 
                max="10"
                type="number" 
                value={pageSize}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (val >= 1 && val <= 10) {
                    setPageSize(val);
                    setCurrentPage(0);
                  }
                }}
                style={{
                  width: '60px',
                  height: '30px',
                  border: 'none',
                  borderBottom: '1px solid #000',
                  borderRadius: '0',
                  textAlign: 'center',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <button 
                type="button" 
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 0 || filteredAddresses.length === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '8px 12px',
                  border: 'none',
                  background: 'transparent',
                  color: (currentPage === 0 || filteredAddresses.length === 0) ? '#ccc' : '#707070',
                  cursor: (currentPage === 0 || filteredAddresses.length === 0) ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="18" height="18" fill="currentColor">
                  <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"></path>
                </svg>
                Previous
              </button>
              <div style={{ width: '1px', height: '30px', backgroundColor: '#e7e7e7' }}></div>
              <button 
                type="button" 
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= numberOfPages() - 1 || filteredAddresses.length === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '8px 12px',
                  border: 'none',
                  background: 'transparent',
                  color: (currentPage >= numberOfPages() - 1 || filteredAddresses.length === 0) ? '#ccc' : '#810055',
                  cursor: (currentPage >= numberOfPages() - 1 || filteredAddresses.length === 0) ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Next
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="18" height="18" fill="currentColor">
                  <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708"></path>
                </svg>
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '14px', color: '#707070' }}>You are on Page No.</span>
                <select 
                  value={filteredAddresses.length > 0 ? currentPage + 1 : 1}
                  onChange={(e) => setCurrentPage(parseInt(e.target.value) - 1)}
                  disabled={filteredAddresses.length === 0}
                  style={{
                    padding: '5px 10px',
                    border: 'none',
                    borderRadius: '0',
                    fontSize: '14px',
                    minWidth: '60px',
                    backgroundColor: 'transparent',
                    outline: 'none'
                  }}
                >
                  {filteredAddresses.length > 0 ? (
                    Array.from({ length: numberOfPages() }, (_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))
                  ) : (
                    <option value={1}>1</option>
                  )}
                </select>
              </div>
              <span style={{ fontSize: '14px', color: '#707070' }}>Total {filteredAddresses.length} Records</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddressMaster;
